'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';
import {
  type ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react';
import {ArrowDropDownIcon, CheckIcon} from '../Icon';
import styles from './SelectDropDown.module.scss';

type SelectProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  placeholder?: string;
  label?: string;
  required?: boolean;
  invalid?: boolean;
  errorText?: string | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  onChange?: (...event: any[]) => void;
  cntstyleprop?: string;
  vpstyleprop?: string;
  className?: string;
  secondaryBackground?: boolean;
  disabled?: boolean;
  tertiaryBackground?: boolean;
};

const SelectDropdownContext = createContext({handleTabPress: () => {}});

export const SelectDropDown = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      placeholder,
      label,
      required,
      invalid,
      errorText,
      onChange,
      cntstyleprop,
      vpstyleprop,
      className,
      disabled,
      ...props
    },
    forwardedRef,
  ): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleTabPress = () => {
      setIsOpen(false);
    };

    const handleOpenChange = (open: boolean) => {
      setIsOpen(!!open);
    };

    return (
      <div className={styles.wrapper}>
        <div className={clsx(className, styles.selectContainer)}>
          <SelectPrimitive.Root
            {...props}
            defaultValue={props.defaultValue}
            value={props.value}
            onValueChange={(value: string) => onChange?.(value)}
            open={isOpen}
            onOpenChange={handleOpenChange}
            required={required || false}
          >
            <SelectPrimitive.Trigger
              className={clsx(
                styles.menuTrigger,
                props.value && styles.highlightColor,
                isOpen && props.value && styles.openColor,
                invalid && styles.hasError,
                disabled && styles.disabled,
              )}
              ref={forwardedRef}
              aria-labelledby="dropdown-label"
            >
              <label
                id="dropdown-label"
                className={clsx(
                  styles.textFieldLabel,
                  props.secondaryBackground && styles.secondaryBackground,
                  props.tertiaryBackground && styles.tertiaryBackground,
                )}
              >
                {label}
              </label>

              {props.value || props.defaultValue ? (
                <SelectPrimitive.Value />
              ) : (
                <span>{!invalid ? placeholder : ''}</span>
              )}

              <SelectPrimitive.Icon className={styles.menuIcon}>
                <ArrowDropDownIcon />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <SelectPrimitive.Content
                position="popper"
                className={clsx(styles.menuContent, cntstyleprop)}
                sideOffset={props.value ? 5 : 0}
              >
                <SelectPrimitive.Viewport
                  className={clsx(
                    styles.Viewport,
                    props.value && styles.highlightColor,
                    vpstyleprop,
                  )}
                >
                  <SelectDropdownContext.Provider value={{handleTabPress}}>
                    {children}
                  </SelectDropdownContext.Provider>
                </SelectPrimitive.Viewport>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        </div>
        {<span className={styles.errorText}>{errorText}</span>}
      </div>
    );
  },
);

type SelectItemProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  indstyleprop?: string;
  itemstyleprop?: string;
  disableDefaultOptionHighlight?: boolean;
  selectedValue?: string;
};

export const SelectItemDropDown = forwardRef<HTMLDivElement, SelectItemProps>(
  (
    {
      children,
      className,
      itemstyleprop,
      indstyleprop,
      disableDefaultOptionHighlight,
      selectedValue,
      ...props
    },
    ref,
  ) => {
    const [noArrowKeyPressed, setNoArrowKeyPressed] = useState<boolean>(true);

    const {handleTabPress} = useContext(SelectDropdownContext);

    /**
     * Keyboard Interactions for Enter Key:
     * When focus is on Select.Trigger, opens the select and focuses the first item.
     * When focus is on an item, selects the focused item.
     *
     * @param {boolean} disableDefaultOptionHighlight - controls whether the first item in the select menu receives initial focus when the menu opens
     *
     * @param {string} selectedValue - the currently selected value. This parameter is required when disableDefaultOptionHighlight is set to true.
     *
     */
    const handleKeyChange = (event: React.KeyboardEvent) => {
      if (event.key === 'Tab') {
        handleTabPress();
      }
      if (disableDefaultOptionHighlight) {
        switch (event.key) {
          case 'ArrowUp':
            setNoArrowKeyPressed(false);
            break;

          case 'ArrowDown':
            if (noArrowKeyPressed && selectedValue?.length === 0) {
              event.preventDefault();
            }
            setNoArrowKeyPressed(false);
            break;

          default: {
            break;
          }
        }
      }
    };

    return (
      <SelectPrimitive.Item
        {...props}
        className={clsx(
          styles.menuItem,
          noArrowKeyPressed &&
            disableDefaultOptionHighlight &&
            styles.disableOptionHighlight,
          className,
        )}
        ref={ref}
        onKeyDown={e => handleKeyChange(e)}
        onKeyUp={e => handleKeyChange(e)}
      >
        <SelectPrimitive.ItemText className={itemstyleprop}>
          {children}
        </SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator
          className={clsx(styles.ItemIndicator, indstyleprop)}
        >
          <CheckIcon className={clsx(styles.iconSize)} />
        </SelectPrimitive.ItemIndicator>
      </SelectPrimitive.Item>
    );
  },
);
